import { EmptyState, SearchField, Skeleton } from '@h2hspark/ui';
import { UserPlus, Users } from 'lucide-react';
import * as React from 'react';

import { useMemberSession } from '@/auth/auth-provider';

import { useMyConnectionRequests, useMyConnections } from './api';
import { ConnectionCard } from './connection-card';
import { ConnectionRequestCard } from './connection-request-card';
import {
  CONNECTIONS_LEAD,
  CONNECTIONS_SEARCH_CLEAR,
  CONNECTIONS_SEARCH_LABEL,
  CONNECTIONS_SEARCH_PLACEHOLDER,
  CONNECTIONS_TITLE,
  NO_CONNECTIONS_DESCRIPTION,
  NO_CONNECTIONS_MATCH_DESCRIPTION,
  NO_CONNECTIONS_MATCH_TITLE,
  NO_CONNECTIONS_TITLE,
  REQUESTS_PANEL_CLEARED,
  REQUESTS_PANEL_LEAD,
  REQUESTS_TITLE,
  matchesConnectionQuery,
  mostRecentRequestFirst,
  newestFirst,
  pendingRequestsCountLabel,
  pendingRequestsPanelCount,
  requestsToggleLabel,
} from './constants';

/**
 * **Connections** — `Web App.dc.html` (10 September), `showConnections`, §2.10.
 *
 * The heading over *"Everyone you've connected with across your events."*, with the **Requests
 * toggle** and the **search pill** on the same baseline at the right; the requests panel beneath
 * the header when it is open; then a **two-column grid** of connection cards, one column on a
 * phone (`MEM-056` AC-2's reference table).
 *
 * ## What this replaced
 *
 * A titled header, a purple banner linking to the requests screen when any were pending, and a
 * single column of rows — the phone prototype's stat-list screen, stretched. Sumit's ruling of
 * 2026-09-11 is one app with a responsive layout, and `Web App.dc.html` is the design at every
 * width.
 *
 * ## The panel and the route both exist, and that is deliberate (`MEM-032` AC-6)
 *
 * The 10 September design opens requests **in place**, under the header. `/app/connections/requests`
 * is not retired with it: AC-6 names the in-app notification as the second entry point to this
 * queue, and a notification needs somewhere to send a member. An address that opens a screen is one
 * a link can carry; a panel that is open only because somebody pressed a button here is not.
 *
 * From this list, the **Requests toggle** is the way in. It is drawn at every width — the same
 * control and the same count — and it opens the panel, which is how AC-6's *"reached from the
 * connections list"* is met.
 *
 * The panel draws what §2.10's `connReqOpen` draws and nothing else: the heading, the pending
 * count, the lead, the cards, and the cleared state. A *"N connection requests"* link to the
 * screen used to sit under the cards; the prototype has no such link, and it was removed per
 * TW 44189288 on 2026-09-24.
 *
 * The panel and the screen render the **same** `ConnectionRequestCard` over the same query, so
 * there is one set of accept and decline controls and one wording of them.
 *
 * ## The count is the third of the ruled badges
 *
 * Sumit, 2026-09-11 (answer 1): a dot on Messages for unread messages, a dot on Your Events for
 * outstanding follow-ups — both `MEM-042`'s — and *"the count of pending requests on Connections'
 * Requests control"*, which is this. A count rather than a dot, because the queue's length is the
 * whole of what a member wants to know before opening it.
 *
 * ## Search is over the list already in hand
 *
 * `GET /api/connections` is the member's whole network in one read (`MEM-033` AC-1), so the query
 * filters what is on the screen rather than putting a round trip between every keystroke and an
 * answer the client has. `matchesConnectionQuery` carries the rule and is tested without a server.
 * The empty state it produces is the prototype's own *"No connections match"*, which is a
 * different sentence from having no connections at all — a member who has just filtered themselves
 * out of their own network must not be told they have none.
 */
export function MemberConnectionsPage(): React.ReactElement {
  const { session } = useMemberSession();
  const signedIn = session !== null;
  const query = useMyConnections({ enabled: signedIn });
  const requests = useMyConnectionRequests({ enabled: signedIn });
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const connections = newestFirst(query.data?.connections ?? []);
  const matching = connections.filter((connection) => matchesConnectionQuery(connection, search));
  const pending = mostRecentRequestFirst(requests.data?.requests ?? []);
  const pendingCount = pending.length;

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <h1 className="mb-1.5 text-2xl tracking-tight md:text-4xl">{CONNECTIONS_TITLE}</h1>
          <p className="max-w-[600px] text-md text-muted-foreground md:text-lg">
            {CONNECTIONS_LEAD}
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          <button
            type="button"
            aria-expanded={panelOpen}
            aria-controls="connection-requests-panel"
            onClick={() => {
              setPanelOpen((open) => !open);
            }}
            className="inline-flex h-11 items-center gap-2 rounded-pill border-[1.5px] border-primary bg-card px-[18px] font-brand text-sm font-bold text-primary transition-colors hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <UserPlus className="h-[17px] w-[17px]" aria-hidden />
            {requestsToggleLabel(panelOpen)}
            {pendingCount === 0 ? null : (
              <span
                aria-label={pendingRequestsCountLabel(pendingCount)}
                className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-brand px-1.5 font-brand text-[11px] font-black text-white"
              >
                {pendingCount}
              </span>
            )}
          </button>

          <SearchField
            size="member"
            value={search}
            onChange={setSearch}
            label={CONNECTIONS_SEARCH_LABEL}
            placeholder={CONNECTIONS_SEARCH_PLACEHOLDER}
            clearLabel={CONNECTIONS_SEARCH_CLEAR}
            className="min-w-0 flex-1 sm:w-[300px] sm:flex-none"
          />
        </div>
      </header>

      {panelOpen ? (
        <section
          id="connection-requests-panel"
          aria-labelledby="connection-requests-heading"
          className="mb-7 rounded-xl border border-border-subtle bg-ivory-100 p-[22px]"
        >
          <div className="mb-1 flex flex-wrap items-baseline gap-3">
            <h2 id="connection-requests-heading" className="text-xl md:text-2xl">
              {REQUESTS_TITLE}
            </h2>
            {pendingCount === 0 ? null : (
              <span className="font-brand text-sm font-bold text-primary">
                {pendingRequestsPanelCount(pendingCount)}
              </span>
            )}
          </div>
          <p className="mb-[18px] text-md text-muted-foreground">{REQUESTS_PANEL_LEAD}</p>

          {requests.isPending ? (
            <div className="space-y-3" aria-hidden>
              <Skeleton className="h-[180px] w-full rounded-lg" />
            </div>
          ) : pendingCount === 0 ? (
            <div className="rounded-lg border border-border-subtle bg-card p-8 text-center">
              <UserPlus className="mx-auto mb-2 h-[30px] w-[30px] text-border-strong" aria-hidden />
              <p className="text-sm text-muted-foreground">{REQUESTS_PANEL_CLEARED}</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {pending.map((request) => (
                <li key={request.requesterRef}>
                  <ConnectionRequestCard request={request} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {query.isPending ? (
        <div className="grid gap-3.5 md:grid-cols-2" aria-hidden>
          <Skeleton className="h-[168px] w-full rounded-lg" />
          <Skeleton className="h-[168px] w-full rounded-lg" />
        </div>
      ) : connections.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" aria-hidden />}
          title={NO_CONNECTIONS_TITLE}
          description={NO_CONNECTIONS_DESCRIPTION}
        />
      ) : matching.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" aria-hidden />}
          title={NO_CONNECTIONS_MATCH_TITLE}
          description={NO_CONNECTIONS_MATCH_DESCRIPTION}
        />
      ) : (
        <ul className="grid items-stretch gap-3.5 md:grid-cols-2">
          {matching.map((connection) => (
            <li key={connection.connectionRef} className="min-w-0">
              <ConnectionCard connection={connection} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
